import { useMemo } from 'react';
import { ResourcePermission, ResourcePermissionRole, TenantShare } from 'src/features/chat/types';
import type { GroupShareInfo } from 'src/components/ui/ShareModal';
import {
  useNodePermissions,
  useShareNode,
  useRevokeNodeShare,
  useRevokeNodeGroupShare,
  useUpdateNodeShare
} from '../api/hooks';
import type { SharedUser, TenantShareResponse } from '../api/types';

interface UseNodeShareProps {
  nodeId?: string;
  permission?: ResourcePermissionRole;
}

// Map API SharedUser to chat types ResourcePermission
function mapToResourcePermission(share: SharedUser): ResourcePermission {
  return {
    permission_id: share.permission_id,
    role: share.permission as ResourcePermissionRole,
    assignee: {
      email: share.email,
      firstname: share.first_name || '',
      surname: share.last_name || '',
      organisation: ''
    },
    granter: {
      email: '',
      firstname: '',
      surname: '',
      organisation: ''
    },
    inherited_permission: share.inherited_permission as ResourcePermissionRole | null
  };
}

// Map API TenantShareResponse to chat types TenantShare
function mapToTenantShare(share: TenantShareResponse): TenantShare {
  return {
    permission_id: share.permission_id,
    role: share.role as ResourcePermissionRole,
    tenant_name: share.tenant_name,
    granter: {
      email: share.granter_email,
      firstname: share.granter_firstname || '',
      surname: share.granter_surname || '',
      organisation: ''
    }
  };
}

/**
 * Hook to manage sharing for any GROUP_CARD node (dashboard or nested group).
 * Uses tanstack query for data fetching and cache invalidation.
 * Only fetches shares if the user has SHARE permission or higher.
 */
const useNodeShare = ({ nodeId, permission }: UseNodeShareProps) => {
  const canFetchShares = !!nodeId && !!permission && permission >= ResourcePermissionRole.SHARE;

  const { data: permissionsData, isLoading: sharesLoading } = useNodePermissions(canFetchShares ? nodeId! : '');

  const shareNodeMutation = useShareNode();
  const revokeNodeShareMutation = useRevokeNodeShare();
  const revokeNodeGroupShareMutation = useRevokeNodeGroupShare();
  const updateNodeShareMutation = useUpdateNodeShare();

  const existingShares = useMemo(
    () => (canFetchShares && permissionsData ? permissionsData.shared_with.map(mapToResourcePermission) : []),
    [canFetchShares, permissionsData]
  );

  const groupShares: GroupShareInfo[] = useMemo(
    () => (canFetchShares && permissionsData?.group_shares ? permissionsData.group_shares : []),
    [canFetchShares, permissionsData]
  );

  const tenantShare = useMemo(
    () => (canFetchShares && permissionsData?.tenant_share ? mapToTenantShare(permissionsData.tenant_share) : null),
    [canFetchShares, permissionsData]
  );

  const inheritedTenantPermission = useMemo(
    () => (canFetchShares && permissionsData ? (permissionsData.inherited_tenant_permission ?? null) : null),
    [canFetchShares, permissionsData]
  );

  const handleAddPeople = async (
    emailList: string[],
    permissionLevel: ResourcePermissionRole,
    message: string,
    groupIds?: string[]
  ) => {
    if (!nodeId) return;
    await shareNodeMutation.mutateAsync({
      nodeId,
      data: {
        emails: emailList.length > 0 ? emailList : undefined,
        permission: permissionLevel,
        message,
        group_ids: groupIds
      }
    });
  };

  const handleShareWithTenant = async (permissionLevel: ResourcePermissionRole) => {
    if (!nodeId) return;
    await shareNodeMutation.mutateAsync({
      nodeId,
      data: { share_with_tenant: true, permission: permissionLevel }
    });
  };

  const handleUpdateTenantShare = async (permissionLevel: ResourcePermissionRole) => {
    if (!nodeId || !tenantShare) return;
    await updateNodeShareMutation.mutateAsync({
      nodeId,
      permissionId: tenantShare.permission_id,
      data: { permission: permissionLevel }
    });
  };

  const handleRemoveTenantShare = async () => {
    if (!nodeId || !tenantShare) return;
    await revokeNodeShareMutation.mutateAsync({
      nodeId,
      permissionId: tenantShare.permission_id
    });
  };

  const handleRemovePerson = async (permissionId: string) => {
    if (!nodeId) return;
    await revokeNodeShareMutation.mutateAsync({ nodeId, permissionId });
  };

  const handleRemoveGroupShare = async (groupId: string) => {
    if (!nodeId) return;
    await revokeNodeGroupShareMutation.mutateAsync({ nodeId, groupId });
  };

  const handleUpdatePermission = async (permissionId: string, permissionLevel: ResourcePermissionRole) => {
    if (!nodeId) return;
    await updateNodeShareMutation.mutateAsync({
      nodeId,
      permissionId,
      data: { permission: permissionLevel }
    });
  };

  return {
    sharesLoading,
    existingShares,
    groupShares,
    tenantShare,
    inheritedTenantPermission,
    handleAddPeople,
    handleRemovePerson,
    handleUpdatePermission,
    handleRemoveGroupShare,
    handleShareWithTenant,
    handleUpdateTenantShare,
    handleRemoveTenantShare
  };
};

export default useNodeShare;
